import { TaxCategoryMappings } from './tax-category-mappings';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { useCountryPacks } from '@/hooks/use-country-packs';
import { taxCategoryLabel, useSetTaxRate, useTaxCategories } from '@/hooks/use-tax-rates';
import { useT } from '@/i18n/use-t';

export function CountryPackTaxRatesPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const packs = useCountryPacks();
  const pack = packs.data?.find((p) => p.id === id);
  const cats = useTaxCategories(id);
  const setRate = useSetTaxRate(id ?? '');
  // H-71: track save state + error per category row so feedback lands
  // on the row the operator just edited, not as one ambiguous banner.
  const [savingCatId, setSavingCatId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  if (id === undefined) return null;

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('countryPacks.taxRates.title', 'Tax rates')}{' '}
            {pack !== undefined && <span>· {pack.displayName}</span>}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'countryPacks.taxRates.description',
              'Active rate per category for {pack}. Allowed values are declared by the country pack; rates can change without a pack release.',
            ).replace(
              '{pack}',
              pack === undefined
                ? t('countryPacks.taxRates.thisPack', 'this pack')
                : t('countryPacks.taxRates.namedPack', 'the {country} pack').replace(
                    '{country}',
                    pack.country,
                  ),
            )}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/country-packs">{t('countryPacks.taxRates.back', 'Back to packs')}</Link>
        </Button>
      </header>

      <div className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>{t('taxMappings.target', 'Category')}</TH>
              <TH>{t('countryPacks.taxRates.colAllowed', 'Allowed rates (%)')}</TH>
              <TH>{t('countryPacks.taxRates.colCurrent', 'Current rate')}</TH>
              <TH>{t('countryPacks.taxRates.colActiveSince', 'Active since')}</TH>
              <TH className="w-48" />
            </TR>
          </THead>
          <TBody>
            {cats.isLoading && (
              <TR>
                <TD colSpan={5} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {cats.data?.categories.map((c) => (
              <TR key={c.id}>
                <TD>
                  <div className="font-medium">{taxCategoryLabel(t, c)}</div>
                  <div className="font-mono text-xs text-muted-foreground">{c.id}</div>
                </TD>
                <TD className="font-mono text-xs">{c.allowedRates.join(', ')}</TD>
                <TD className="font-mono">
                  {c.currentRate === null ? (
                    <span className="text-destructive">
                      {t('countryPacks.taxRates.unset', 'unset')}
                    </span>
                  ) : (
                    `${c.currentRate}%`
                  )}
                </TD>
                <TD className="text-xs text-muted-foreground">
                  {c.currentRateActiveSince === null
                    ? '—'
                    : new Date(c.currentRateActiveSince).toLocaleString()}
                </TD>
                <TD className="text-right">
                  <Select
                    className="w-32"
                    value={c.currentRate === null ? '' : String(c.currentRate)}
                    // Only the row being saved is disabled — other rows stay editable.
                    disabled={savingCatId === c.id}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === '') return;
                      const ratePercent = Number(next);
                      if (ratePercent === c.currentRate) return;
                      setSavingCatId(c.id);
                      setRowErrors((prev) => {
                        const { [c.id]: _drop, ...rest } = prev;
                        return rest;
                      });
                      setRate
                        .mutateAsync({ taxCategory: c.id, ratePercent })
                        .catch((err: unknown) =>
                          setRowErrors((prev) => ({ ...prev, [c.id]: extractError(err) })),
                        )
                        .finally(() => setSavingCatId((cur) => (cur === c.id ? null : cur)));
                    }}
                  >
                    {c.currentRate === null && <option value="">—</option>}
                    {c.allowedRates.map((r) => (
                      <option key={r} value={r}>
                        {r}%
                      </option>
                    ))}
                  </Select>
                  {savingCatId === c.id && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('common.saving', 'Saving…')}
                    </div>
                  )}
                  {rowErrors[c.id] !== undefined && (
                    <div className="mt-1 text-xs text-destructive">{rowErrors[c.id]}</div>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <TaxCategoryMappings key={id} countryPackId={id} categories={cats.data?.categories ?? []} />
    </div>
  );
}

function extractError(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return 'Unexpected error';
}
